import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ShieldCheck } from "lucide-react";

export default function AdminLogin() {
  const { adminLogin, formatApiError } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await adminLogin(email, password);
      navigate("/admin/dashboard", { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-void flex items-center justify-center px-6" data-testid="admin-login-page">
      <div className="w-full max-w-md animate-slide-up">
        <div className="flex items-center gap-3 mb-2">
          <div className="p-2.5 rounded-lg bg-[#FFE600]/10">
            <ShieldCheck className="w-7 h-7 text-[#FFE600]" />
          </div>
          <p className="font-mono text-xs text-[#FFE600] tracking-[0.3em] uppercase">School Admin</p>
        </div>
        <h1 className="font-heading text-4xl sm:text-5xl font-black text-white tracking-tighter mb-2">Principal Login</h1>
        <p className="font-body text-sm text-white/40 mb-8">View-only access to every class, student and weekly report.</p>
        <form onSubmit={submit} className="glass-surface rounded-xl p-6 space-y-5 border border-[#FFE600]/15">
          <div>
            <Label htmlFor="admin-email" className="text-white/70 text-sm">Email</Label>
            <Input id="admin-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              className="mt-2 bg-black/40 border-white/10 text-white" data-testid="admin-login-email" />
          </div>
          <div>
            <Label htmlFor="admin-password" className="text-white/70 text-sm">Password</Label>
            <Input id="admin-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
              className="mt-2 bg-black/40 border-white/10 text-white" data-testid="admin-login-password" />
          </div>
          {error && <p className="font-body text-sm text-magenta" data-testid="admin-login-error">{error}</p>}
          <button type="submit" disabled={loading} data-testid="admin-login-submit"
            className="w-full h-11 rounded-full bg-[#FFE600] text-black font-heading text-sm tracking-wider font-bold hover:bg-[#FFE600]/85 disabled:opacity-50 transition-colors duration-200">
            {loading ? "SIGNING IN..." : "SIGN IN"}
          </button>
        </form>
        <button onClick={() => navigate("/login")} className="mt-6 font-body text-xs text-white/30 hover:text-white/60 transition-colors duration-200" data-testid="admin-login-back">
          Student or teacher? Go to the main login
        </button>
      </div>
    </div>
  );
}
